/**
 * Ausfertigung als PDF je Fassung (Befund 9, 32): Agentur mit Anlage, Marke und Creator jeweils ohne die vor ihnen verborgene
 * Anlage, mit Unterschriften und Protokoll. Erzeugt im Browser mit dem PDF-Schreiber der Fachlogik (renderContractPdf);
 * dieselbe Funktion nutzen Detail, Druckansicht, Signierseite und Creator-Portal.
 */
import {
  AUTHORITY_LABEL, renderContractPdf, renderSignatureProtocolPdf, SIGNING_AUTHORITIES,
  type ContractPdfImage, type ContractPdfModel, type ContractPdfSignature, type ContractRole, type Language, type SigningAuthority,
} from "@talentkontor/domain";
import { sanitizeHtml } from "../../../lib/sicheres-html";
import { day, paperDates, PROTOCOL_TEXT, protocolFromEvents, protocolRows, stamp, type Fassung, type ProtocolEvent } from "./protokoll";
import { VOID_KIND_LABEL } from "./modell";
import { inLang } from "../../../lib/sprache";
import type { VoidKind } from "./daten";
import { inkCanvas } from "./tinte";

export interface AusfertigungSigner {
  role: ContractRole; slot: number; capacity: "party" | "legal_guardian"; name: string | null;
  signed: boolean; signing_name: string | null; signing_position: string | null; signing_authority: string | null;
  signed_at: string | null; signature_data: string | null; paper?: boolean;
  /** Datum auf dem Papier ('YYYY-MM-DD'); fehlt es, kommt es aus dem Protokoll (Signierseite, Creator-Portal) */
  paper_signed_on?: string | null;
}

export interface AusfertigungInput {
  view: Fassung;
  language: Language;
  title: string;
  numberLabel: string | null;
  agencyName: string;
  timeZone: string;
  status: string;
  sentAt: string | null;
  completedAt: string | null;
  voidedAt?: string | null;
  voidKind?: VoidKind | null;
  terminatedOn?: string | null;
  /** schon je Fassung: die Anlage ist null, wenn sie vor dieser Fassung verborgen ist */
  html: string | null;
  annexHtml: string | null;
  signers: readonly AusfertigungSigner[];
  events: readonly ProtocolEvent[];
  documentSha: string | null;
  finalSha: string | null;
  viewSha?: string | null;
  /** Logo der Agentur als JPEG (invoiceLogo) für den Dokumentkopf; ohne Logo kein Kopfbild */
  logo?: ContractPdfImage | null;
  /** Pfad des Agenturlogos im Bucket branding (org_settings.logo_path); wird geladen, wenn logo fehlt */
  logoPath?: string | null;
  /** Agenturfarbe (org_settings.brand_color) für die Kopfzeile, wie in der Vorschau */
  accent?: string | null;
  /** PDF-Vorlage für Verträge (nur Gestaltung, 0456): geltendeVorlage bzw. contract_public_view.layout */
  layout?: unknown;
  /**
   * Entwurf oder Vorlage (K-513): Vermerk „Entwurf – nicht unterschrieben“ auf jeder Seite und als Hinweis auf Seite 1,
   * ohne Protokoll (es gibt noch keine Beweiskette). „sample“: Vorlage mit Beispielwerten statt echter Stammdaten.
   */
  draft?: "draft" | "sample" | null;
}

/** Vermerk auf jeder Seite eines Entwurfs oder einer Vorlage (K-513), in der Sprache des Vertrags. */
export const ENTWURF_VERMERK: Record<Language, string> = { de: "Entwurf – nicht unterschrieben", en: "Draft – not signed" };
const ENTWURF_HINWEIS: Record<Language, Record<"draft" | "sample", string>> = {
  de: {
    draft: "Entwurf – nicht unterschrieben. Diese Vorschau ist noch kein Vertrag; Nummer, Datum und Unterschriften kommen erst mit dem Versand.",
    sample: "Entwurf – nicht unterschrieben. Vorlage mit Beispielwerten; im echten Vertrag stehen die Stammdaten der Parteien.",
  },
  en: {
    draft: "Draft – not signed. This preview is not a contract yet; number, date and signatures are only added on sending.",
    sample: "Draft – not signed. Template with sample values; the actual contract contains the parties' master data.",
  },
};

/** Logo der Agentur für den Dokumentkopf des PDFs: mitgegeben oder aus dem Bucket branding geladen (nur im Browser). */
async function logoOf(input: AusfertigungInput): Promise<ContractPdfImage | null> {
  if (input.logo !== undefined) return input.logo;
  if (!input.logoPath || typeof document === "undefined") return null;
  const { invoiceLogo } = await import("../../../model/invoice-document");
  return (await invoiceLogo(input.logoPath).catch(() => null))?.image ?? null;
}

const BANNER = {
  de: {
    void: (d: string, k: string) => `${k} am ${d}. Bis dahin war der Vertrag mit den unten stehenden Unterschriften wirksam.`,
    terminated: (d: string) => `Gekündigt zum ${d}.`,
    open: "Noch nicht von allen Parteien unterschrieben (Zwischenstand).",
    paperSigned: "auf Papier unterschrieben",
  },
  en: {
    void: (d: string, k: string) => `${k} on ${d}. Until then the contract was in force with the signatures below.`,
    terminated: (d: string) => `Terminated with effect from ${d}.`,
    open: "Not yet signed by all parties (interim version).",
    paperSigned: "signed on paper",
  },
} as const;
const VOID_EN: Record<VoidKind, string> = { withdrawn: "Withdrawn", error: "Recorded in error", mutual: "Cancelled by mutual agreement", superseded: "Replaced by a new version" };

function authority(a: string | null, lang: Language): string | null {
  return a && (SIGNING_AUTHORITIES as readonly string[]).includes(a) ? AUTHORITY_LABEL[a as SigningAuthority][lang] : null;
}

function dateOnly(iso: string, tz: string, lang: Language): string {
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso);
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "de-DE", { dateStyle: "long", timeZone: tz }).format(d);
}

/**
 * Unterschriftsbild (PNG) als JPEG auf weißem Grund für das PDF. Ohne Browser (Tests) oder bei einem Fehler: null; dann
 * steht der Name in Schrift.
 */
export async function pngToJpeg(dataUrl: string | null): Promise<ContractPdfImage | null> {
  if (!dataUrl || typeof document === "undefined" || !/^data:image\/png;base64,/.test(dataUrl)) return null;
  try {
    // helle Tinte (ältere Profil-Unterschrift aus dem Dunkelmodus) erst dunkel umfärben, sonst ist sie auf Weiß
    // unsichtbar (SICHT-CREATOR-23); die gespeicherte Unterschrift selbst bleibt unverändert
    const ink = await inkCanvas(dataUrl);
    if (!ink) return null;
    const canvas = document.createElement("canvas");
    canvas.width = ink.canvas.width;
    canvas.height = ink.canvas.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(ink.canvas, 0, 0);
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", 0.9));
    if (!blob) return null;
    return { jpeg: new Uint8Array(await blob.arrayBuffer()), width: canvas.width, height: canvas.height };
  } catch {
    return null;
  }
}

/** Unterschriftsfelder mit Beschriftung, Funktion, Befugnis, Zeitpunkt und Bild (Ausfertigung und Unterschriftenprotokoll). */
async function signaturesFor(input: AusfertigungInput, opts: { images?: boolean }): Promise<ContractPdfSignature[]> {
  const lang = input.language;
  const t = PROTOCOL_TEXT[lang];
  const many = (r: ContractRole) => input.signers.filter((s) => s.role === r).length > 1;
  const onPaper = paperDates(input.events);
  const signatures: ContractPdfSignature[] = [];
  for (const s of [...input.signers].sort((a, c) => a.role.localeCompare(c.role) || a.slot - c.slot)) {
    const label = `${t.roles[s.role]}${many(s.role) ? ` ${s.slot}` : ""}${s.capacity === "legal_guardian" ? ` · ${t.guardian}` : ""}`;
    signatures.push({
      role: s.role, slot: s.slot, label, signed: s.signed, name: s.name,
      signingName: s.signing_name, detail: [s.signing_position, authority(s.signing_authority, lang)].filter(Boolean).join(", ") || null,
      signedAt: s.signed && s.signed_at ? stamp(s.signed_at, input.timeZone, lang, true) : null, paper: !!s.paper,
      paperSignedOn: s.signed && s.paper ? (() => { const d = s.paper_signed_on ?? onPaper.get(`${s.role}#${s.slot}`); return d ? day(d, lang) : null; })() : null,
      image: s.signed && opts.images !== false ? await pngToJpeg(s.signature_data) : null,
    });
  }
  return signatures;
}

/** Hinweis oben auf Seite 1: aufgehoben, gekündigt oder Zwischenstand. */
function bannerFor(input: AusfertigungInput): string | null {
  const lang = input.language;
  const b = BANNER[lang];
  const tz = input.timeZone;
  return input.status === "void" && input.voidedAt
    ? b.void(dateOnly(input.voidedAt, tz, lang), input.voidKind ? (lang === "en" ? VOID_EN[input.voidKind] : inLang("de", () => VOID_KIND_LABEL[input.voidKind!])) : (lang === "en" ? "Cancelled" : "Aufgehoben"))
    : input.terminatedOn ? b.terminated(dateOnly(input.terminatedOn, tz, lang))
    : input.status !== "signed" ? b.open : null;
}

/**
 * Unterschriftenprotokoll eines Vertrags „Dokument der Marke“ (0071) als eigenes PDF: Unterschriftsseite mit allen
 * Unterschriften und Bildern, Prüfwert des Originals, Ereignisse mit Zeitpunkten. Das Original bleibt unverändert daneben.
 */
export async function unterschriftenprotokollPdf(input: AusfertigungInput & { document: { name: string; size: number | null; sha256: string } },
  opts: { images?: boolean; now?: Date } = {}): Promise<Uint8Array> {
  const lang = input.language;
  const tz = input.timeZone;
  return renderSignatureProtocolPdf({
    language: lang, title: input.title, numberLabel: input.numberLabel, agencyName: input.agencyName, document: input.document,
    coverHtml: sanitizeHtml(input.html ?? ""), signatures: await signaturesFor(input, opts),
    rows: protocolRows(input.events, lang, tz, input.view), timeZone: tz,
    sentAt: input.sentAt ? stamp(input.sentAt, tz, lang) : null, completedAt: input.completedAt ? stamp(input.completedAt, tz, lang) : null,
    documentSha: input.documentSha, finalSha: input.finalSha, banner: bannerFor(input),
  }, { now: opts.now });
}

/** Dateiname des Unterschriftenprotokolls, z. B. „Kooperationsvertrag V-0009 (Unterschriftenprotokoll).pdf“. */
export function protokollFileName(title: string, number: string | null, lang: Language): string {
  return `${[title, number].filter(Boolean).join(" ")} (${lang === "en" ? "Signature record" : "Unterschriftenprotokoll"}).pdf`.replace(/[\\/:*?"<>|]+/g, "-");
}

/** Ausfertigung einer Fassung als PDF-Bytes. */
export async function ausfertigungPdf(input: AusfertigungInput, opts: { images?: boolean; now?: Date } = {}): Promise<Uint8Array> {
  return renderContractPdf(await ausfertigungModell(input, opts), { now: opts.now });
}

/** Seitenmodell der Ausfertigung (für das PDF und für Tests): Kopfzeile, Hinweis, Vermerk, Unterschriften, Protokoll. */
export async function ausfertigungModell(input: AusfertigungInput, opts: { images?: boolean } = {}): Promise<ContractPdfModel> {
  const lang = input.language;
  const t = PROTOCOL_TEXT[lang];
  const tz = input.timeZone;
  const number = input.numberLabel ?? "–";
  const signatures = await signaturesFor(input, opts);
  if (input.draft) {
    // Entwurf oder Vorlage (K-513): ohne Nummer, wenn noch keine vergeben ist, und ohne Protokoll
    return {
      language: lang,
      title: `${input.title}${input.numberLabel ? ` · ${input.numberLabel}` : ""}`,
      header: [input.agencyName, input.numberLabel, `${t.fassung[input.view]}${input.annexHtml?.trim() ? t.withAnnex : ""}`].filter(Boolean).join(" · "),
      banner: ENTWURF_HINWEIS[lang][input.draft],
      stamp: ENTWURF_VERMERK[lang],
      logo: await logoOf(input),
      accent: input.accent ?? null,
      layout: input.layout ?? null,
      bodyHtml: sanitizeHtml(input.html ?? ""),
      annexHtml: input.annexHtml ? sanitizeHtml(input.annexHtml) : null,
      signatures,
      protocol: null,
      labels: { paper: BANNER[lang].paperSigned },
    };
  }
  const banner = bannerFor(input);
  const hashes = [
    input.documentSha ? `${t.atSend}: ${input.documentSha}` : null,
    input.finalSha ? `${t.atEnd}: ${input.finalSha}` : null,
    input.viewSha && input.viewSha !== input.documentSha ? `${t.seenBy}: ${input.viewSha}` : null,
  ].filter((x): x is string => !!x);
  return {
    language: lang,
    title: `${input.title}${input.numberLabel ? ` · ${input.numberLabel}` : ""}`,
    header: `${input.agencyName} · ${number} · ${t.fassung[input.view]}${input.annexHtml?.trim() ? t.withAnnex : ""}`,
    banner,
    logo: await logoOf(input),
    accent: input.accent ?? null,
    layout: input.layout ?? null,
    bodyHtml: sanitizeHtml(input.html ?? ""),
    annexHtml: input.annexHtml ? sanitizeHtml(input.annexHtml) : null,
    signatures,
    protocol: {
      title: t.title,
      intro: t.intro(input.title, number, stamp(input.sentAt, tz, lang), input.completedAt ? stamp(input.completedAt, tz, lang) : null),
      hashes,
      columns: [`${t.cols[0]} (${tz})`, t.cols[1], t.cols[2], t.cols[3]],
      rows: protocolRows(input.events, lang, tz, input.view),
      how: { title: t.how, text: t.howText },
    },
    labels: { paper: BANNER[lang].paperSigned },
  };
}

/** Dateiname der Fassung, z. B. „Kooperationsvertrag Herbst V-0009 (Creator).pdf“. */
export function ausfertigungFileName(title: string, number: string | null, view: Fassung, lang: Language): string {
  const suffix = { agency: lang === "en" ? "Agency" : "Agentur", brand: lang === "en" ? "Brand" : "Marke", talent: "Creator" }[view];
  return `${[title, number].filter(Boolean).join(" ")} (${suffix}).pdf`.replace(/[\\/:*?"<>|]+/g, "-");
}

/** Dateiname eines Entwurfs oder einer Vorlage, z. B. „Kooperationsvertrag (Entwurf, Marke).pdf“ (K-513). */
export function entwurfFileName(title: string, view: Fassung, lang: Language): string {
  const suffix = { agency: lang === "en" ? "Agency" : "Agentur", brand: lang === "en" ? "Brand" : "Marke", talent: "Creator" }[view];
  return `${title.trim() || (lang === "en" ? "Contract" : "Vertrag")} (${lang === "en" ? "Draft" : "Entwurf"}, ${suffix}).pdf`.replace(/[\\/:*?"<>|]+/g, "-");
}

/** Datei im Browser speichern. */
export function saveFile(bytes: Uint8Array | Blob, name: string, type = "application/pdf"): void {
  const blob = bytes instanceof Blob ? bytes : new Blob([bytes as BlobPart], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Eingabe der Ausfertigung aus dem geladenen Vertrag (Team): Anlage je Fassung, Protokoll aus der Beweiskette. */
export function ausfertigungAusDetail(d: {
  contract: { title: string; language: Language; contract_number_label: string | null; status: string; sent_at: string | null; completed_at: string | null;
    voided_at: string | null; void_kind: VoidKind | null; terminated_on: string | null; rendered_html: string | null; rendered_annex_html: string | null;
    annex_hidden_from: ContractRole[]; document_sha256: string | null; final_sha256: string | null };
  signers: readonly { id: string; role: ContractRole; slot: number; capacity: "party" | "legal_guardian"; name: string; signed_at: string | null; invalidated_at: string | null;
    signing_name: string | null; signing_position: string | null; signing_authority: string | null; signature_data: string | null; paper_signed_on?: string | null }[];
  events: readonly { id: number; event: string; at: string; signer_id: string | null; view_sha256: string | null; main_sha256?: string | null;
    client_info: Record<string, string> | null; details: Record<string, unknown> | null }[];
}, view: Fassung, agencyName: string, timeZone: string): AusfertigungInput {
  const c = d.contract;
  const hidden = view !== "agency" && c.annex_hidden_from.includes(view);
  // 0117 (Prüfung 0–2, Befund 6): Die Fassung einer Partei ohne Sicht auf den vertraulichen Teil nennt nur Prüfwerte des
  // Hauptteils (ihrer Fassung beim Versand, bei Abschluss und je Ereignis), nie einen Wert, der den verborgenen Teil einschließt
  const roleOf = (signerId: string | null) => d.signers.find((s) => s.id === signerId)?.role ?? null;
  const partAtSend = d.events.find((e) => e.event === "sent" && roleOf(e.signer_id) === view)?.view_sha256 ?? null;
  const partAtEnd = [...d.events].reverse().find((e) => e.event === "completed")?.main_sha256 ?? null;
  const events = hidden ? d.events.map((e) => ({ ...e, view_sha256: e.event === "document_read" ? e.view_sha256 : e.main_sha256 ?? null })) : d.events;
  return {
    view, language: c.language, title: c.title, numberLabel: c.contract_number_label, agencyName, timeZone, status: c.status,
    sentAt: c.sent_at, completedAt: c.completed_at, voidedAt: c.voided_at, voidKind: c.void_kind, terminatedOn: c.terminated_on,
    html: c.rendered_html, annexHtml: hidden ? null : c.rendered_annex_html,
    signers: d.signers.map((s) => {
      const signed = !!s.signed_at && !s.invalidated_at;
      return { role: s.role, slot: s.slot, capacity: s.capacity, name: s.name, signed, signing_name: signed ? s.signing_name : null,
        signing_position: signed ? s.signing_position : null, signing_authority: signed ? s.signing_authority : null, signed_at: signed ? s.signed_at : null,
        signature_data: signed ? s.signature_data : null, paper: signed && !!s.paper_signed_on, paper_signed_on: signed ? s.paper_signed_on ?? null : null };
    }),
    events: protocolFromEvents(events, d.signers),
    documentSha: hidden ? partAtSend : c.document_sha256, finalSha: hidden ? (c.final_sha256 ? partAtEnd : null) : c.final_sha256,
  };
}
